import {
  Account,
  Challenge,
  LoginAttemptResult,
  Referer,
  UserAccount,
} from '../models';
import { IAccountResponseError } from '../../account.data';
import { ITrackingAction, ITrackingObject } from 'i2g-ng-tracking';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';

// Actions
export enum AccountActionsEnum {
  FETCH_REFERER = '[ACCOUNT] FETCH_REFERER',
  FETCH_REFERER_SUCCESS = '[ACCOUNT] FETCH_REFERER_SUCCESS',
  FETCH_REFERER_ERROR = '[ACCOUNT] FETCH_REFERER_ERROR',
  REGISTER_ACCOUNT_EMAIL = '[ACCOUNT] REGISTER_ACCOUNT_EMAIL',
  REGISTER_ACCOUNT_GOOGLE = '[ACCOUNT] REGISTER_ACCOUNT_GOOGLE',
  REGISTER_ACCOUNT_FACEBOOK = '[ACCOUNT] REGISTER_ACCOUNT_FACEBOOK',
  REGISTER_ACCOUNT_APPLE = '[ACCOUNT] REGISTER_ACCOUNT_APPLE',
  REGISTER_ACCOUNT_SUCCESS = '[ACCOUNT] REGISTER_ACCOUNT_SUCCESS',
  REGISTER_ACCOUNT_ERROR = '[ACCOUNT] REGISTER_ACCOUNT_ERROR',
  SET_RETURN_URL = '[ACCOUNT] SET_RETURN_URL',
  LOGIN_ACCOUNT_EMAIL = '[ACCOUNT] LOGIN_ACCOUNT_EMAIL',
  LOGIN_ACCOUNT_FACEBOOK = '[ACCOUNT] LOGIN_ACCOUNT_FACEBOOK',
  LOGIN_ACCOUNT_GOOGLE = '[ACCOUNT] LOGIN_ACCOUNT_GOOGLE',
  LOGIN_ACCOUNT_APPLE = '[ACCOUNT] LOGIN_ACCOUNT_APPLE',
  LOGIN_ACCOUNT_COMPANY = '[ACCOUNT] LOGIN_ACCOUNT_COMPANY',
  LOGIN_ACCOUNT_BY_CHALLENGE = '[ACCOUNT] LOGIN_ACCOUNT_BY_CHALLENGE',
  LOGIN_ACCOUNT_BY_CHALLENGE_ERROR = '[ACCOUNT] LOGIN_ACCOUNT_BY_CHALLENGE_ERROR',
  LOGIN_ACCOUNT_SUCCESS = '[ACCOUNT] LOGIN_ACCOUNT_SUCCESS',
  LOGIN_ACCOUNT_ERROR = '[ACCOUNT] LOGIN_ACCOUNT_ERROR',
  LOGIN_SELECT_ACCOUNT = '[ACCOUNT] LOGIN_SELECT_ACCOUNT',
  SET_PASSWORD_EMAIL = '[ACCOUNT] SET_PASSWORD_EMAIL',
  SET_PASSWORD_SUCCESS = '[ACCOUNT] SET_PASSWORD_SUCCESS',
  SET_PASSWORD_ERROR = '[ACCOUNT] SET_PASSWORD_ERROR',
  SET_PASSWORD_TOKEN = '[ACCOUNT] SET_PASSWORD_TOKEN',
  SET_PASSWORD_TOKEN_SUCCESS = '[ACCOUNT] SET_PASSWORD_TOKEN_SUCCESS',
  SET_PASSWORD_TOKEN_ERROR = '[ACCOUNT] SET_PASSWORD_TOKEN_ERROR',
  UPDATE_PASSWORD_TOKEN = '[ACCOUNT] UPDATE_PASSWORD_TOKEN',
  UPDATE_PASSWORD_TOKEN_SUCCESS = '[ACCOUNT] UPDATE_PASSWORD_TOKEN_SUCCESS',
  CHANGE_EMAIL_TOKEN = '[ACCOUNT] CHANGE_EMAIL_TOKEN',
  CHANGE_EMAIL_TOKEN_SUCCESS = '[ACCOUNT] CHANGE_EMAIL_TOKEN_SUCCESS',
  CHANGE_EMAIL_TOKEN_ERROR = '[ACCOUNT] CHANGE_EMAIL_TOKEN_ERROR',
  CANCEL_CHANGE_EMAIL_TOKEN = '[ACCOUNT] CANCEL_CHANGE_EMAIL_TOKEN',
  PROMPT_UNUSUAL_ACTIVITY = '[ACCOUNT] PROMPT_UNUSUAL_ACTIVITY',
  RESEND_UNUSUAL_LOGIN_EMAIL = '[ACCOUNT] RESEND_UNUSUAL_LOGIN_EMAIL',
  RESEND_UNUSUAL_LOGIN_EMAIL_SUCCESS = '[ACCOUNT] RESEND_UNUSUAL_LOGIN_EMAIL_SUCCESS',
  RESEND_UNUSUAL_LOGIN_EMAIL_ERROR = '[ACCOUNT] RESEND_UNUSUAL_LOGIN_EMAIL_ERROR',
  VERIFY_UNUSUAL_LOGIN = '[ACCOUNT] VERIFY_UNUSUAL_LOGIN',
  VERIFY_UNUSUAL_LOGIN_SUCCESS = '[ACCOUNT] VERIFY_UNUSUAL_LOGIN_SUCCESS',
  VERIFY_UNUSUAL_LOGIN_ERROR = '[ACCOUNT] VERIFY_UNUSUAL_LOGIN_ERROR',
  SET_TRACKING_OBJECT = '[ACCOUNT] SET_TRACKING_OBJECT',
  TRACK_EVENT = '[ACCOUNT] TRACK_EVENT',
  GO_TO_LOGIN = '[ACCOUNT] GO_TO_LOGIN',
}

export interface IReferralCode {
  referralCode: string;
}

export interface IRefererPayload {
  referer: Referer;
}

export interface IAccountPayload {
  account: Account;
}

export interface ITrackingScreenName {
  trackingScreenName: TrackingScreenNamesEnum;
}

export interface IRegisterAccountSuccessPayload
  extends IAccountPayload,
    ITrackingScreenName {}

export interface IUserAccountPayload {
  userAccount: UserAccount;
}

export interface ILoginAccountSuccessPayload
  extends IUserAccountPayload,
    ITrackingScreenName {
  accountId: string;
}

export interface IErrorPayload {
  error: Error | IAccountResponseError;
}

export interface IAccountErrorPayload
  extends IErrorPayload,
    ITrackingScreenName {}

export interface IRegisterPayload {
  email: string;
  locale: string;
}

export interface IRegisterAccountEmailPayload
  extends IRegisterPayload,
    ITrackingScreenName {
  password: string;
}

export interface ISocialSignUpPayload
  extends IRegisterPayload,
    ITrackingScreenName {
  idToken: string;
}

export interface IReturnUrl {
  returnUrl: string;
}

export interface ILoginPayload {
  email: string;
  locale: string;
}

export interface ILoginAccountEmailPayload
  extends ILoginPayload,
    ITrackingScreenName {
  password: string;
}

export interface ISocialLoginPayload
  extends ILoginPayload,
    ITrackingScreenName {
  idToken: string;
}

export interface ICompanyLoginPayload extends ITrackingScreenName {
  accountId: string;
  refreshToken: string;
  locale: string;
}

export interface ISetPasswordPayload {
  email: string;
  locale: string;
}

export interface IEmailPayload {
  email: string;
}

export interface IMessagePayload {
  message: string;
}

export interface ITokenPayload {
  token: string;
}

export interface ISetPasswordTokenPayload extends ITokenPayload {
  locale: string;
}

export interface ISetPasswordTokenSuccessPayload extends IEmailPayload {
  userId: string;
  value: string;
}

export interface IUpdatePasswordToken extends ISetPasswordTokenPayload {
  userId: string;
  password: string;
}

export interface IChangeEmailTokenPayload extends ITokenPayload {
  locale: string;
}

export interface IChangeEmailTokenSuccessPayload {
  value: string;
}

export interface IChangeEmailErrorPayload {
  error: Error | IAccountResponseError;
}

export interface IChallengePayload {
  challenge: Challenge;
}

export interface IChallengeLoginPayload
  extends IChallengePayload,
    ITrackingScreenName {
  locale: string;
}

export interface IAttemptIdPayload {
  attemptId: string;
}

export interface IVerifyUnusualLogin extends IAttemptIdPayload {
  verificationCode: string;
}

export interface IVerifyUnusualLoginSuccess {
  loginAttemptResult: LoginAttemptResult;
}

export interface SetTrackingObjectPayload {
  trackingObject: ITrackingObject;
}

export interface TrackEventPayload {
  trackingAction: ITrackingAction;
}

export interface FetchReferer {
  type: AccountActionsEnum.FETCH_REFERER;
  payload: IReferralCode;
}

export interface FetchRefererSuccess {
  type: AccountActionsEnum.FETCH_REFERER_SUCCESS;
  payload: IRefererPayload;
}

export interface FetchRefererError {
  type: AccountActionsEnum.FETCH_REFERER_ERROR;
  payload: IErrorPayload;
}

export interface RegisterAccountByEmail {
  type: AccountActionsEnum.REGISTER_ACCOUNT_EMAIL;
  payload: IRegisterAccountEmailPayload;
}

export interface RegisterAccountByGoogle {
  type: AccountActionsEnum.REGISTER_ACCOUNT_GOOGLE;
  payload: ISocialSignUpPayload;
}

export interface RegisterAccountByFacebook {
  type: AccountActionsEnum.REGISTER_ACCOUNT_FACEBOOK;
  payload: ISocialSignUpPayload;
}

export interface RegisterAccountByApple {
  type: AccountActionsEnum.REGISTER_ACCOUNT_APPLE;
  payload: ISocialSignUpPayload;
}

export interface RegisterAccountSuccess {
  type: AccountActionsEnum.REGISTER_ACCOUNT_SUCCESS;
  payload: IRegisterAccountSuccessPayload;
}

export interface RegisterAccountError {
  type: AccountActionsEnum.REGISTER_ACCOUNT_ERROR;
  payload: IAccountErrorPayload;
}

export interface SetReturnUrl {
  type: AccountActionsEnum.SET_RETURN_URL;
  payload: IReturnUrl;
}

export interface LoginAccountByEmail {
  type: AccountActionsEnum.LOGIN_ACCOUNT_EMAIL;
  payload: ILoginAccountEmailPayload;
}

export interface LoginAccountByFacebook {
  type: AccountActionsEnum.LOGIN_ACCOUNT_FACEBOOK;
  payload: ISocialLoginPayload;
}

export interface LoginAccountByGoogle {
  type: AccountActionsEnum.LOGIN_ACCOUNT_GOOGLE;
  payload: ISocialLoginPayload;
}

export interface LoginAccountByApple {
  type: AccountActionsEnum.LOGIN_ACCOUNT_APPLE;
  payload: ISocialLoginPayload;
}

export interface LoginAccountByCompany {
  type: AccountActionsEnum.LOGIN_ACCOUNT_COMPANY;
  payload: ICompanyLoginPayload;
}

export interface LoginAccountByChallenge {
  type: AccountActionsEnum.LOGIN_ACCOUNT_BY_CHALLENGE;
  payload: IChallengeLoginPayload;
}

export interface LoginAccountByChallengeError {
  type: AccountActionsEnum.LOGIN_ACCOUNT_BY_CHALLENGE_ERROR;
}

export interface LoginAccountSuccess {
  type: AccountActionsEnum.LOGIN_ACCOUNT_SUCCESS;
  payload: ILoginAccountSuccessPayload;
}

export interface LoginAccountError {
  type: AccountActionsEnum.LOGIN_ACCOUNT_ERROR;
  payload: IAccountErrorPayload;
}

export interface LoginSelectAccount {
  type: AccountActionsEnum.LOGIN_SELECT_ACCOUNT;
  payload: IUserAccountPayload;
}

export interface SetPasswordByEmail {
  type: AccountActionsEnum.SET_PASSWORD_EMAIL;
  payload: ISetPasswordPayload;
}

export interface SetPasswordSuccess {
  type: AccountActionsEnum.SET_PASSWORD_SUCCESS;
  payload: IMessagePayload;
}

export interface SetPasswordError {
  type: AccountActionsEnum.SET_PASSWORD_ERROR;
  payload: IErrorPayload;
}

export interface SetPasswordToken {
  type: AccountActionsEnum.SET_PASSWORD_TOKEN;
  payload: ISetPasswordTokenPayload;
}

export interface SetPasswordTokenSuccess {
  type: AccountActionsEnum.SET_PASSWORD_TOKEN_SUCCESS;
  payload: ISetPasswordTokenSuccessPayload;
}

export interface SetPasswordTokenError {
  type: AccountActionsEnum.SET_PASSWORD_TOKEN_ERROR;
  payload: IErrorPayload;
}

export interface UpdatePasswordToken {
  type: AccountActionsEnum.UPDATE_PASSWORD_TOKEN;
  payload: IUpdatePasswordToken;
}

export interface UpdatePasswordTokenSuccess {
  type: AccountActionsEnum.UPDATE_PASSWORD_TOKEN_SUCCESS;
}

export interface ChangeEmailToken {
  type: AccountActionsEnum.CHANGE_EMAIL_TOKEN;
  payload: IChangeEmailTokenPayload;
}

export interface ChangeEmailTokenSuccess {
  type: AccountActionsEnum.CHANGE_EMAIL_TOKEN_SUCCESS;
  payload: IChangeEmailTokenSuccessPayload;
}

export interface ChangeEmailTokenError {
  type: AccountActionsEnum.CHANGE_EMAIL_TOKEN_ERROR;
  payload: IChangeEmailErrorPayload;
}

export interface CancelChangeEmailToken {
  type: AccountActionsEnum.CANCEL_CHANGE_EMAIL_TOKEN;
  payload: IChangeEmailTokenPayload;
}

export interface PromptUnusualActivity {
  type: AccountActionsEnum.PROMPT_UNUSUAL_ACTIVITY;
  payload: IChallengePayload;
}

export interface ResendUnusualLoginEmail {
  type: AccountActionsEnum.RESEND_UNUSUAL_LOGIN_EMAIL;
  payload: IAttemptIdPayload;
}

export interface ResendUnusualLoginEmailSuccess {
  type: AccountActionsEnum.RESEND_UNUSUAL_LOGIN_EMAIL_SUCCESS;
  payload: IMessagePayload;
}

export interface ResendUnusualLoginEmailError {
  type: AccountActionsEnum.RESEND_UNUSUAL_LOGIN_EMAIL_ERROR;
  payload: IMessagePayload;
}

export interface VerifyUnusualLogin {
  type: AccountActionsEnum.VERIFY_UNUSUAL_LOGIN;
  payload: IVerifyUnusualLogin;
}

export interface VerifyUnusualLoginSuccess {
  type: AccountActionsEnum.VERIFY_UNUSUAL_LOGIN_SUCCESS;
  payload: IVerifyUnusualLoginSuccess;
}

export interface VerifyUnusualLoginError {
  type: AccountActionsEnum.VERIFY_UNUSUAL_LOGIN_ERROR;
  payload: IMessagePayload;
}

export interface SetTrackingObject {
  type: AccountActionsEnum.SET_TRACKING_OBJECT;
  payload: SetTrackingObjectPayload;
}

export interface TrackEvent {
  type: AccountActionsEnum.TRACK_EVENT;
  payload: TrackEventPayload;
}

export interface GoToLogin {
  type: AccountActionsEnum.GO_TO_LOGIN;
}

// Action Creators

export const fetchReferer = (referralCode: string): FetchReferer => {
  return {
    type: AccountActionsEnum.FETCH_REFERER,
    payload: {
      referralCode,
    },
  };
};

export const fetchRefererSuccess = (referer: Referer): FetchRefererSuccess => {
  return {
    type: AccountActionsEnum.FETCH_REFERER_SUCCESS,
    payload: {
      referer,
    },
  };
};

export const fetchRefererError = (error: any): FetchRefererError => {
  return {
    type: AccountActionsEnum.FETCH_REFERER_ERROR,
    payload: {
      error,
    },
  };
};

export const registerAccountByEmail = (
  email: string,
  password: string,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): RegisterAccountByEmail => {
  return {
    type: AccountActionsEnum.REGISTER_ACCOUNT_EMAIL,
    payload: {
      email,
      password,
      locale,
      trackingScreenName,
    },
  };
};

export const registerAccountByGoogle = (
  email: string,
  idToken: string,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): RegisterAccountByGoogle => {
  return {
    type: AccountActionsEnum.REGISTER_ACCOUNT_GOOGLE,
    payload: {
      email,
      idToken,
      locale,
      trackingScreenName,
    },
  };
};

export const registerAccountByFacebook = (
  email: string,
  idToken: string,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): RegisterAccountByFacebook => {
  return {
    type: AccountActionsEnum.REGISTER_ACCOUNT_FACEBOOK,
    payload: {
      email,
      idToken,
      locale,
      trackingScreenName,
    },
  };
};

export const registerAccountByApple = (
  email: string,
  idToken: string,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): RegisterAccountByApple => {
  return {
    type: AccountActionsEnum.REGISTER_ACCOUNT_APPLE,
    payload: {
      email,
      idToken,
      locale,
      trackingScreenName,
    },
  };
};

export const registerAccountSuccess = (
  account: Account,
  trackingScreenName: TrackingScreenNamesEnum
): RegisterAccountSuccess => {
  return {
    type: AccountActionsEnum.REGISTER_ACCOUNT_SUCCESS,
    payload: {
      account,
      trackingScreenName,
    },
  };
};

export const registerAccountError = (
  error: IAccountResponseError | Error,
  trackingScreenName: TrackingScreenNamesEnum
): RegisterAccountError => {
  return {
    type: AccountActionsEnum.REGISTER_ACCOUNT_ERROR,
    payload: {
      error,
      trackingScreenName,
    },
  };
};

export const setReturnUrl = (returnUrl: string): SetReturnUrl => {
  return {
    type: AccountActionsEnum.SET_RETURN_URL,
    payload: {
      returnUrl,
    },
  };
};

export const loginAccountByEmail = (
  email: string,
  password: string,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): LoginAccountByEmail => {
  return {
    type: AccountActionsEnum.LOGIN_ACCOUNT_EMAIL,
    payload: {
      email,
      password,
      locale,
      trackingScreenName,
    },
  };
};

export const loginAccountByFacebook = (
  email: string,
  idToken: string,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): LoginAccountByFacebook => {
  return {
    type: AccountActionsEnum.LOGIN_ACCOUNT_FACEBOOK,
    payload: {
      email,
      idToken,
      locale,
      trackingScreenName,
    },
  };
};

export const loginAccountByGoogle = (
  email: string,
  idToken: string,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): LoginAccountByGoogle => {
  return {
    type: AccountActionsEnum.LOGIN_ACCOUNT_GOOGLE,
    payload: {
      email,
      idToken,
      locale,
      trackingScreenName,
    },
  };
};

export const loginAccountByApple = (
  email: string,
  idToken: string,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): LoginAccountByApple => {
  return {
    type: AccountActionsEnum.LOGIN_ACCOUNT_APPLE,
    payload: {
      email,
      idToken,
      locale,
      trackingScreenName,
    },
  };
};

export const loginAccountByCompany = (
  accountId: string,
  refreshToken: string,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): LoginAccountByCompany => {
  return {
    type: AccountActionsEnum.LOGIN_ACCOUNT_COMPANY,
    payload: {
      accountId,
      refreshToken,
      locale,
      trackingScreenName,
    },
  };
};

export const loginAccountByChallenge = (
  challenge: Challenge,
  locale: string,
  trackingScreenName: TrackingScreenNamesEnum
): LoginAccountByChallenge => {
  return {
    type: AccountActionsEnum.LOGIN_ACCOUNT_BY_CHALLENGE,
    payload: {
      challenge,
      locale,
      trackingScreenName,
    },
  };
};

export const loginAccountByChallengeError = (): LoginAccountByChallengeError => {
  return {
    type: AccountActionsEnum.LOGIN_ACCOUNT_BY_CHALLENGE_ERROR,
  };
};

export const loginAccountSuccess = (
  userAccount: UserAccount,
  accountId: string,
  trackingScreenName: TrackingScreenNamesEnum
): LoginAccountSuccess => {
  return {
    type: AccountActionsEnum.LOGIN_ACCOUNT_SUCCESS,
    payload: {
      userAccount,
      accountId,
      trackingScreenName,
    },
  };
};

export const loginAccountError = (
  error: IAccountResponseError | Error,
  trackingScreenName: TrackingScreenNamesEnum
): LoginAccountError => {
  return {
    type: AccountActionsEnum.LOGIN_ACCOUNT_ERROR,
    payload: {
      error,
      trackingScreenName,
    },
  };
};

export const loginSelectAccount = (
  userAccount: UserAccount
): LoginSelectAccount => {
  return {
    type: AccountActionsEnum.LOGIN_SELECT_ACCOUNT,
    payload: {
      userAccount,
    },
  };
};

export const setPasswordByEmail = (
  email: string,
  locale: string
): SetPasswordByEmail => {
  return {
    type: AccountActionsEnum.SET_PASSWORD_EMAIL,
    payload: {
      email,
      locale,
    },
  };
};

export const setPasswordSuccess = (message: string): SetPasswordSuccess => {
  return {
    type: AccountActionsEnum.SET_PASSWORD_SUCCESS,
    payload: {
      message,
    },
  };
};

export const setPasswordError = (
  error: IAccountResponseError | Error
): SetPasswordError => {
  return {
    type: AccountActionsEnum.SET_PASSWORD_ERROR,
    payload: {
      error,
    },
  };
};

export const setPasswordToken = (
  token: string,
  locale: string
): SetPasswordToken => {
  return {
    type: AccountActionsEnum.SET_PASSWORD_TOKEN,
    payload: {
      token,
      locale,
    },
  };
};

export const setPasswordTokenSuccess = (
  value: string,
  email: string,
  userId: string
): SetPasswordTokenSuccess => {
  return {
    type: AccountActionsEnum.SET_PASSWORD_TOKEN_SUCCESS,
    payload: {
      value,
      email,
      userId,
    },
  };
};

export const setPasswordTokenError = (
  error: IAccountResponseError | Error
): SetPasswordTokenError => {
  return {
    type: AccountActionsEnum.SET_PASSWORD_TOKEN_ERROR,
    payload: {
      error,
    },
  };
};

export const updatePasswordToken = (
  userId: string,
  token: string,
  password: string,
  locale: string
): UpdatePasswordToken => {
  return {
    type: AccountActionsEnum.UPDATE_PASSWORD_TOKEN,
    payload: {
      userId,
      token,
      password,
      locale,
    },
  };
};

export const updatePasswordTokenSuccess = (): UpdatePasswordTokenSuccess => {
  return {
    type: AccountActionsEnum.UPDATE_PASSWORD_TOKEN_SUCCESS,
  };
};

export const changeEmailToken = (
  token: string,
  locale: string
): ChangeEmailToken => {
  return {
    type: AccountActionsEnum.CHANGE_EMAIL_TOKEN,
    payload: {
      token,
      locale,
    },
  };
};

export const changeEmailTokenSuccess = (
  token: string
): ChangeEmailTokenSuccess => {
  return {
    type: AccountActionsEnum.CHANGE_EMAIL_TOKEN_SUCCESS,
    payload: {
      value: token,
    },
  };
};

export const changeEmailTokenError = (
  error: IAccountResponseError | Error
): ChangeEmailTokenError => {
  return {
    type: AccountActionsEnum.CHANGE_EMAIL_TOKEN_ERROR,
    payload: {
      error,
    },
  };
};

export const cancelChangeEmailToken = (
  token: string,
  locale: string
): CancelChangeEmailToken => {
  return {
    type: AccountActionsEnum.CANCEL_CHANGE_EMAIL_TOKEN,
    payload: {
      token,
      locale,
    },
  };
};

export const promptUnusualActivity = (
  challenge: Challenge
): PromptUnusualActivity => {
  return {
    type: AccountActionsEnum.PROMPT_UNUSUAL_ACTIVITY,
    payload: {
      challenge,
    },
  };
};

export const resendUnusualLoginEmail = (
  attemptId: string
): ResendUnusualLoginEmail => {
  return {
    type: AccountActionsEnum.RESEND_UNUSUAL_LOGIN_EMAIL,
    payload: {
      attemptId,
    },
  };
};

export const resendUnusualLoginEmailSuccess = (
  message: string
): ResendUnusualLoginEmailSuccess => {
  return {
    type: AccountActionsEnum.RESEND_UNUSUAL_LOGIN_EMAIL_SUCCESS,
    payload: {
      message,
    },
  };
};

export const resendUnusualLoginEmailError = (
  message: string
): ResendUnusualLoginEmailError => {
  return {
    type: AccountActionsEnum.RESEND_UNUSUAL_LOGIN_EMAIL_ERROR,
    payload: {
      message,
    },
  };
};

export const verifyUnusualLogin = (
  attemptId: string,
  verificationCode: string
): VerifyUnusualLogin => {
  return {
    type: AccountActionsEnum.VERIFY_UNUSUAL_LOGIN,
    payload: {
      attemptId,
      verificationCode,
    },
  };
};

export const verifyUnusualLoginSuccess = (
  loginAttemptResult: LoginAttemptResult
): VerifyUnusualLoginSuccess => {
  return {
    type: AccountActionsEnum.VERIFY_UNUSUAL_LOGIN_SUCCESS,
    payload: {
      loginAttemptResult,
    },
  };
};

export const verifyUnusualLoginError = (
  message: string
): VerifyUnusualLoginError => {
  return {
    type: AccountActionsEnum.VERIFY_UNUSUAL_LOGIN_ERROR,
    payload: {
      message,
    },
  };
};

export const setTrackingObject = (
  trackingObject: ITrackingObject
): SetTrackingObject => {
  return {
    type: AccountActionsEnum.SET_TRACKING_OBJECT,
    payload: {
      trackingObject,
    },
  };
};

export const trackEvent = (trackingAction: ITrackingAction): TrackEvent => {
  return {
    type: AccountActionsEnum.TRACK_EVENT,
    payload: {
      trackingAction,
    },
  };
};

export const goToLogin = (): GoToLogin => {
  return {
    type: AccountActionsEnum.GO_TO_LOGIN,
  };
};
